import { Routes } from '@angular/router';
import { authenticationGuard } from '~/app/core/auth/guards/auth.guard';
import { rootGuard } from '~/app/core/auth/guards/root.guard';
import { scopeCanActivateGuard } from '~/app/core/auth/guards/scope.guard';
import { FullLayoutComponent } from '~/app/core/layouts/full-layout.component';
import { AppAuthenticationResolver } from '~/app/core/resolver/app-authentication.resolver';
import { AppConfigResolver } from '~/app/core/resolver/app-config.resolver';
import { ErrorPageComponent } from '~/app/error-page.component';
import { deleteDocumentGuard } from '~/app/features/document/delete-document.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'feed',
    pathMatch: 'full',
  },
  {
    path: '.well-known/change-password',
    redirectTo: 'account/profile',
    pathMatch: 'full',
  },
  {
    component: FullLayoutComponent,
    path: '',
    resolve: [AppAuthenticationResolver, AppConfigResolver],
    children: [
      {
        path: 'auth',
        canActivate: [rootGuard],
        children: [
          {
            loadComponent: () => import('./core/auth/login/login.component').then((m) => m.LoginComponent),
            path: 'login',
          },
          {
            loadComponent: () => import('./core/auth/login/login.component').then((m) => m.LoginComponent),
            path: 'confirm-registration',
          },
          {
            loadComponent: () =>
              import('./core/auth/login/components/domain-login/domain-login.component').then(
                (m) => m.DomainLoginComponent,
              ),
            path: 'domain-login',
          },
          {
            loadComponent: () =>
              import('./core/auth/login/components/domain-login/domain-login.component').then(
                (m) => m.DomainLoginComponent,
              ),
            path: 'confirm-domain-registration',
          },
          {
            loadComponent: () =>
              import('./core/auth/registration/registration.component').then((m) => m.RegistrationComponent),
            path: 'register',
          },
          {
            loadComponent: () =>
              import('./core/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
            path: 'reset-password',
          },
        ],
      },
      {
        path: 'account/profile',
        loadComponent: () => import('./core/account/profile/profile.component').then((m) => m.ProfileComponent),
        canActivate: [authenticationGuard],
      },
      {
        path: 'home',
        loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
      },
      {
        path: 'site',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/site/cms-site.component').then((m) => m.CMSSiteComponent),
          },
          {
            path: 'list',
            loadComponent: () =>
              import('./features/site/list/cms-site-list.component').then((m) => m.CmsSiteListComponent),
          },
          {
            path: 'add',
            loadComponent: () => import('./features/site/cms-site-form.component').then((m) => m.CMSSiteFormComponent),
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['cmssite.create', 'mgmt.cmssite.create'] },
          },
          {
            path: ':siteId/edit',
            loadComponent: () => import('./features/site/cms-site-form.component').then((m) => m.CMSSiteFormComponent),
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['cmssite.update', 'mgmt.cmssite.update'] },
          },
          {
            path: ':siteId',
            loadComponent: () => import('./features/site/cms-site.component').then((m) => m.CMSSiteComponent),
          },
        ],
      },
      {
        path: 'feed',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/feed/feed.component').then((m) => m.FeedComponent),
          },
          {
            path: 'user-created/:userId',
            loadComponent: () =>
              import('./features/feed/user-feed/user-feed.component').then((m) => m.UserFeedComponent),
          },
          {
            path: 'subscriptions',
            loadComponent: () =>
              import('./features/feed/feed-subscriptions/feed-subscriptions.component').then(
                (m) => m.FeedSubscriptionsComponent,
              ),
            canActivate: [authenticationGuard],
          },
          {
            path: 'add',
            loadComponent: () =>
              import('./features/feed/feed-post/feed-post-form.component').then((m) => m.FeedPostFormComponent),
            data: { scopes: ['feedpost.create', 'mgmt.feedpost.create'] },
            canActivate: [scopeCanActivateGuard],
          },
          {
            path: ':feedPostId/edit',
            loadComponent: () =>
              import('./features/feed/feed-post/feed-post-form.component').then((m) => m.FeedPostFormComponent),
            data: { scopes: ['feedpost.update', 'mgmt.feedpost.update'] },
            canActivate: [scopeCanActivateGuard],
          },
          {
            path: ':feedPostId',
            loadComponent: () =>
              import('./features/feed/feed-post/feed-post.component').then((m) => m.FeedPostComponent),
            canActivate: [scopeCanActivateGuard],
          },
        ],
      },
      {
        path: 'mgmt',
        canActivate: [authenticationGuard],
        children: [
          {
            path: 'tag',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () => import('./features/mgmt/tag/tag-list.component').then((m) => m.TagListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.tag.read'] },
              },
              {
                path: 'add',
                loadComponent: () => import('./features/mgmt/tag/tag-form.component').then((m) => m.TagFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.tag.create'] },
              },
              {
                path: ':tagId/edit',
                loadComponent: () => import('./features/mgmt/tag/tag-form.component').then((m) => m.TagFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.tag.update'] },
              },
            ],
          },
          {
            path: 'account',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/account/account-list.component').then((m) => m.AccountListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.account.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/account/account-form.component').then((m) => m.AccountFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.account.create'] },
              },
              {
                path: ':accountId/edit',
                loadComponent: () =>
                  import('./features/mgmt/account/account-form.component').then((m) => m.AccountFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.account.update'] },
              },
            ],
          },
          {
            path: 'user',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/user/user-list.component').then((m) => m.UserListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.user.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/user/user-form.component').then((m) => m.UserFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.user.create'] },
              },
              {
                path: ':userId/edit',
                loadComponent: () =>
                  import('./features/mgmt/user/user-form.component').then((m) => m.UserFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.user.update'] },
              },
              {
                path: ':userId/update-password',
                loadComponent: () =>
                  import('./features/mgmt/user/user-update-password-form.component').then(
                    (m) => m.UserUpdatePasswordFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.user.update'] },
              },
            ],
          },
          {
            path: 'client',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/client/client-list.component').then((m) => m.ClientListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.client.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/client/client-form.component').then((m) => m.ClientFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.client.create'] },
              },
              {
                path: ':clientId/edit',
                loadComponent: () =>
                  import('./features/mgmt/client/client-form.component').then((m) => m.ClientFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.client.update'] },
              },
            ],
          },
          {
            path: 'role',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/role/role-list.component').then((m) => m.RoleListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.role.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/role/role-form.component').then((m) => m.RoleFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.role.create'] },
              },
              {
                path: ':roleId/edit',
                loadComponent: () =>
                  import('./features/mgmt/role/role-form.component').then((m) => m.RoleFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.role.update'] },
              },
            ],
          },
          {
            path: 'scope',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/scope/scope-list.component').then((m) => m.ScopeListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.scope.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/scope/scope-form.component').then((m) => m.ScopeFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.scope.create'] },
              },
              {
                path: ':scopeId/edit',
                loadComponent: () =>
                  import('./features/mgmt/scope/scope-form.component').then((m) => m.ScopeFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.scope.update'] },
              },
            ],
          },
          {
            path: 'context',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/context/context-list.component').then((m) => m.ContextListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.context.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/context/context-form.component').then((m) => m.ContextFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.context.create'] },
              },
              {
                path: ':contextId/edit',
                loadComponent: () =>
                  import('./features/mgmt/context/context-form.component').then((m) => m.ContextFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.context.update'] },
              },
            ],
          },
          {
            path: 'attribute',
            canActivate: [authenticationGuard, scopeCanActivateGuard],
            loadComponent: () =>
              import('./features/mgmt/attribute/attribute.component').then((m) => m.AttributeComponent),
            data: { scopes: ['mgmt.attribute.read', 'mgmt.attribute.update'] },
            children: [
              {
                path: ':attributeId/values',
                loadComponent: () =>
                  import('./features/mgmt/attribute/attribute.component').then((m) => m.AttributeComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.attribute.read'] },
              },
              {
                path: ':attributeId/values/add',
                loadComponent: () =>
                  import('./features/mgmt/attribute/attribute.component').then((m) => m.AttributeComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.attribute.create'] },
              },
              {
                path: ':attributeId/values/:attributeValueId/edit',
                loadComponent: () =>
                  import('./features/mgmt/attribute/attribute.component').then((m) => m.AttributeComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.attribute.update'] },
              },
            ],
          },
          {
            path: 'language',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/language/language-list.component').then((m) => m.LanguageListComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.lang.read', 'mgmt.system.lang.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/language/language-form.component').then((m) => m.LanguageFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.system.lang.create'] },
              },
              {
                path: ':languageId/edit',
                loadComponent: () =>
                  import('./features/mgmt/language/language-form.component').then((m) => m.LanguageFormComponent),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.lang.update', 'mgmt.system.lang.update'] },
              },
            ],
          },
          {
            path: 'backend-setting',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/backend-setting/components/backend-setting-list/backend-setting-list.component').then(
                    (m) => m.BackendSettingListComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.config.read', 'mgmt.system.config.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/backend-setting/components/backend-setting-form/backend-setting-form.component').then(
                    (m) => m.BackendSettingFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.system.config.create'] },
              },
              {
                path: ':backendSettingId/edit',
                loadComponent: () =>
                  import('./features/mgmt/backend-setting/components/backend-setting-form/backend-setting-form.component').then(
                    (m) => m.BackendSettingFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.config.update', 'mgmt.system.config.update'] },
              },
            ],
          },
          {
            path: 'translation',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/translation/components/translation-list/translation-list.component').then(
                    (m) => m.TranslationListComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.translation.read'] },
              },
              {
                path: 'add',
                loadComponent: () =>
                  import('./features/mgmt/translation/components/translation-form/translation-form.component').then(
                    (m) => m.TranslationFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.translation.create'] },
              },
              {
                path: ':translationId/edit',
                loadComponent: () =>
                  import('./features/mgmt/translation/components/translation-form/translation-form.component').then(
                    (m) => m.TranslationFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.translation.update'] },
              },
            ],
          },
          {
            path: 'report-content',
            canActivate: [authenticationGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/mgmt/report-content/report-content-list.component').then(
                    (m) => m.ReportContentListComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.reportcontent.read'] },
              },
              {
                path: ':reportContentId/edit',
                loadComponent: () =>
                  import('./features/mgmt/report-content/report-content-form.component').then(
                    (m) => m.ReportContentFormComponent,
                  ),
                canActivate: [scopeCanActivateGuard],
                data: { scopes: ['mgmt.reportcontent.update'] },
              },
            ],
          },
        ],
      },
      {
        path: 'mediadirectories',
        canActivate: [authenticationGuard],
        children: [
          {
            loadComponent: () =>
              import('./features/document/mediadirectory-form.component').then((m) => m.MediaDirectoryFormComponent),
            path: '',
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['mgmt.mediadirectory.create'] },
          },
          {
            loadComponent: () =>
              import('./features/document/mediadirectory-form.component').then((m) => m.MediaDirectoryFormComponent),
            path: 'add',
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['mgmt.mediadirectory.create'] },
          },
          {
            loadComponent: () =>
              import('./features/document/mediadirectory-form.component').then((m) => m.MediaDirectoryFormComponent),
            path: ':directoryId/edit',
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['mgmt.mediadirectory.update'] },
          },
          {
            loadComponent: () => import('./features/document/document.component').then((m) => m.DocumentComponent),
            path: ':directoryId',
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['mediadirectory.read', 'mgmt.mediadirectory.read'] },
          },
          {
            loadComponent: () =>
              import('./features/document/document-form.component').then((m) => m.DocumentFormComponent),
            path: ':directoryId/add',
            canActivate: [scopeCanActivateGuard],
            data: { scopes: ['mediafile.create', 'mgmt.mediafile.create'] },
          },
          {
            path: ':directoryId/:documentId/edit',
            loadComponent: () =>
              import('./features/document/document-form.component').then((m) => m.DocumentFormComponent),
            canActivate: [scopeCanActivateGuard],
            canDeactivate: [deleteDocumentGuard],
            data: { scopes: ['mediafile.update', 'mgmt.mediafile.update'] },
          },
        ],
      },
      {
        path: 'employee',
        loadComponent: () => import('./features/employee/employee.component').then((m) => m.EmployeeComponent),
        canActivate: [authenticationGuard],
        data: { scopes: ['employee.read'] },
      },
    ],
  },
  {
    component: ErrorPageComponent,
    path: 'error',
  },
  {
    component: ErrorPageComponent,
    path: '**',
  },
];
